/**
 * @module app.core
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { MemberService } from 'app/cd/services/member-service';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/essette-api';
import { validate } from 'app/shared/elements/ess-rule';
import { I18N } from 'aurelia-i18n';
import { IApiActionResults } from 'app/shared/lib/api/api-types';
import { forceNull } from 'app/shared/lib/utilities';
import * as CD from 'app/cd/models/essette-api';
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class SaveMember extends ComponentBase {
    ////****************** Fields and Properties ************************************////
    protected member: CD.Member | null = null;
    protected memberEligibility: CD.MemberEligibility | null = null;
    protected memberPlanCustomValues: Core.CustomValues | null = null;

    protected memberId: string | null = null;
    protected memberPlan: CD.MemberPlan | null = null;
    protected validateForm = validate;

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly memberService: MemberService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    ////****************** Events ***************************************************////
    protected bind(): void {
        if(!this.member) this.clear();
    }


    ////****************** Functions ************************************************////
    protected clear(): void {
        this.member = {
            addresses: [],
            custom: {
                text: {},
                lookup: {}
            }
        };
        this.memberEligibility = {};
        this.memberId = null;

        this.memberPlan = null;
        this.memberPlanCustomValues = {
            text: {},
            lookup: {}
        };
    }

    // Strip extraneous fields from the UDF Text values and ensure that no empty strings are passed to API
    protected parseTextContainer(text: CD.CustomFieldDefinitionTextContainer | undefined): CD.CustomFieldDefinitionTextContainer | undefined {
        if(!text) return text;
        const VALUE_FIELD = 'value';
        let newText: CD.CustomFieldDefinitionTextContainer = {};
        Object.entries(text).forEach((field: [string, any]) => {
            const fieldKey = field[0];
            if(fieldKey.includes(VALUE_FIELD)) {
                newText = {
                    ...newText,
                    [fieldKey]: forceNull(field[1])
                };
            }
        });

        return newText;
    }

    // Strip extraneous fields from the UDF Lookup Values
    protected parseLookupContainer(lookup: CD.CustomFieldDefinitionLookupContainer | undefined): CD.CustomFieldDefinitionLookupContainer | undefined {
        if(!lookup) return lookup;
        const CHOICE_FIELD = 'choice';
        let newLookup: CD.CustomFieldDefinitionLookupContainer = {};
        Object.entries(lookup).forEach((field: [string, any]) => {
            const fieldKey = field[0];
            if(fieldKey.includes(CHOICE_FIELD)) {
                newLookup = {
                    ...newLookup,
                    [fieldKey]: field[1] ? { code: field[1].code } : null
                };
            }
        });

        return newLookup;
    }

    // Parse member and build new member without extraneous fields
    protected buildMember(): CD.Member {
        const member = this.member;
        let saveMember: CD.Member = {};
        if(!!member) {
            saveMember = {
                // Member Information
                identifier: {
                    medicaid: forceNull(member.identifier?.medicaid),
                    socialSecurityNumber: forceNull(member.identifier?.socialSecurityNumber),
                    external: forceNull(member.identifier?.external)
                },
                name: {
                    first: forceNull(member.name?.first),
                    middle: forceNull(member.name?.middle),
                    last: forceNull(member.name?.last)
                },
                gender: member.gender ? { code: member.gender.code } : null,
                birthDate: member.birthDate,
                deathDate: member.deathDate,
                maritalStatus: member.maritalStatus ? { code: member.maritalStatus.code } : null,
                language: member.language ? {
                    reads: member.language.reads ? { code: member.language.reads.code } : null,
                    speaks: member.language.speaks ? { code: member.language.speaks.code } : null,
                } : undefined,
                race: member.race ? { code: member.race.code } : null,
                ethnicity: member.ethnicity ? { code: member.ethnicity.code } : null,
                emailAddress: forceNull(member.emailAddress),
                // Demographics
                demographics: {
                    pronouns: forceNull(member.demographics?.pronouns),
                    preferredName: forceNull(member.demographics?.preferredName),
                    gender: member.demographics?.gender ? { code: member.demographics.gender.code } : null,
                    race: member.demographics?.race ? { code: member.demographics.race.code } : null,
                    disability: forceNull(member.demographics?.disability),
                    hasDurablePowerOfAttorney: member.demographics?.hasDurablePowerOfAttorney,
                    hasAdvanceDirective: member.demographics?.hasAdvanceDirective,
                    advanceDirectiveExpirationDate: member.demographics?.advanceDirectiveExpirationDate
                },
                // Health Plan and Eligibility
                history: {
                    eligibility: this.memberEligibility ? [{
                        product: {
                            code: this.memberEligibility.product?.code,
                            healthPlan: this.memberEligibility.product?.healthPlan ? { id: this.memberEligibility.product.healthPlan.id } : null,
                            lineOfBusiness: this.memberEligibility.product?.lineOfBusiness ? { id: this.memberEligibility.product.lineOfBusiness.id } : null
                        },
                        dateRange: this.memberEligibility.dateRange,
                        planRank: this.memberEligibility.planRank,
                        planDisplayIdentifier: forceNull(this.memberEligibility.planDisplayIdentifier),
                        receivedDate: this.memberEligibility.receivedDate,
                        shareOfCostAmount: this.memberEligibility.shareOfCostAmount,
                        employer: forceNull(this.memberEligibility.employer),
                        groupNumber: forceNull(this.memberEligibility.groupNumber)
                    }] : undefined
                },
                // Phone Numbers
                phoneNumber: {
                    home: member.phoneNumber?.home?.number ? member.phoneNumber.home : null,
                    cell: member.phoneNumber?.cell?.number ? member.phoneNumber.cell : null,
                    work: member.phoneNumber?.work?.number ? member.phoneNumber.work : null,
                    fax: member.phoneNumber?.fax?.number ? member.phoneNumber.fax : null
                },
                // Member UDFs
                custom: {
                    text: this.parseTextContainer(member.custom?.text),
                    lookup: this.parseLookupContainer(member.custom?.lookup)
                }
            };
        }

        return saveMember;
    }

    // Parse Member Plan UDFs and remove extraneous fields
    protected buildMemberPlan(): CD.MemberPlan {
        return {
            id: this.memberPlan?.id,
            custom: {
                text: this.parseTextContainer(this.memberPlanCustomValues?.text),
                lookup: this.parseLookupContainer(this.memberPlanCustomValues?.lookup)
            }
        };
    }

    // Parse Addresses and remove extraneous fields
    protected buildAddresses(): CD.MemberAddress[] {
        return this.member?.addresses?.map((address) => ({
            type: address.type ? { id: address.type.id } : null,
            member: { id: this.memberId },
            street1: forceNull(address.street1),
            street2: forceNull(address.street2),
            city: forceNull(address.city),
            region: address.region ? { code: address.region.code } : null,
            postalCode: forceNull(address.postalCode)
        })) || [];
    }

    protected async submit(): Promise<void> {
        const memberSaved = await this.saveMember();
        await this.saveAddresses(); // Member insert only supports home/mailing/work addresses, so we add these separately
        await this.loadMemberPlan();
        await this.saveMemberPlanUDFs();
        if(memberSaved) {
            await this.router.navigateToRoute('core.admin.member', { memberId: this.memberId });
        }

    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async saveMember(): Promise<boolean> {
        if(!this.member) return false;
        let isSuccessful = false;
        let errorMessage: string = '';
        try {
            this.state.beginSave();
            const response = await this.memberService.saveMember(this.buildMember());
            this.memberId = response.id;
            isSuccessful = response.isSuccessful;
        }
        catch(error) {
            if(error.description) { errorMessage = error.description; }
            throw error;
        }
        finally {
            this.toaster.standard.saved('cd:member.$', isSuccessful);
            if(!isSuccessful) {
                const exception = (this.toaster.toast())
                    .title('Detail')
                    .class('ess-toast-error')
                    .icon('ui:icons.error')
                    .message(errorMessage)
                exception.show();
            }
            this.state.endSave();
            return isSuccessful;
        }
    }

    @apiCall
    private async saveAddresses(): Promise<boolean> {
        if(!this.memberId || !this.member?.addresses) return false;
        let isSuccessful = false;
        let errorMessage: string = "";
        const promises = new Array<Promise<IApiActionResults>>();

        try {
            this.state.beginSave();

            // Create a promise for each address
            this.buildAddresses().forEach(address => {
                promises.push(this.memberService.saveMemberAddress(address));
            });
            // Execute all promises and wait until they all finish
            const responses = await Promise.all(promises);
            isSuccessful = responses.every(r => (r.isSuccessful));
        }
        catch(error) {
            if(error.description) { errorMessage = error.description; }
            throw error;
        }
        finally {
            if(!isSuccessful) {
                this.toaster.standard.saved('cd:member.address.$', isSuccessful);
                const exception = (this.toaster.toast())
                    .title('Detail')
                    .class('ess-toast-error')
                    .icon('ui:icons.error')
                    .message(errorMessage)
                exception.show();
            }
            this.state.endSave();
            return isSuccessful;
        }
    }

    @apiCall
    private async loadMemberPlan(): Promise<void> {
        if(!this.memberId) return;
        try {
            this.state.beginLoad();

            const response = await this.memberService.loadPlansByMember(this.memberId);
            if(response.results.length > 0) this.memberPlan = response.results[0];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async saveMemberPlanUDFs(): Promise<boolean> {
        let isSuccessful = false;
        let errorMessage: string = "";
        try {
            this.state.beginSave();
            if(this.memberPlan) {
                const response = await this.memberService.saveMemberPlan(this.buildMemberPlan());
                isSuccessful = response.isSuccessful;
            } else {
                errorMessage = this.i18n.tr('ui:messages.errors.load', { what: this.i18n.tr('cd:member.plan.$long') });
            }
        }
        catch(error) {
            if(error.description) { errorMessage = error.description; }
            throw error;
        }
        finally {
            this.toaster.standard.saved('cd:customField.plan.$long', isSuccessful, 2);
            if(!isSuccessful) {
                const exception = (this.toaster.toast())
                    .title('Detail')
                    .class('ess-toast-error')
                    .icon('ui:icons.error')
                    .message(errorMessage)
                exception.show();
            }
            this.state.endSave();
            return isSuccessful;
        }
    }
}
